import api from './api';
import { getBackendOrigin } from '../utils/apiBase';

const BACKEND_ORIGIN = getBackendOrigin(process.env.REACT_APP_API_URL);

/**
 * Authentication service for handling user login, logout, and session management
 */
export const authService = {
  /**
   * Initiate Reddit OAuth login
   * Redirects user to backend OAuth endpoint
   */
  loginWithReddit: () => {
    window.location.href = `${BACKEND_ORIGIN}/api/auth/reddit/login/`;
  },

  /**
   * Initiate Facebook OAuth login
   */
  loginWithFacebook: () => {
    window.location.href = `${BACKEND_ORIGIN}/api/auth/facebook/login/`;
  },

  /**
   * Initiate YouTube OAuth login
   */
  loginWithYouTube: () => {
    window.location.href = `${BACKEND_ORIGIN}/api/auth/youtube/login/`;
  },

  /**
   * Start Data Portability flow to export all YouTube comments (including on other channels' videos).
   * User must already be logged in. Redirects to Google to grant "Move a copy of your YouTube comments".
   */
  startYouTubeCommentsExport: () => {
    window.location.href = `${BACKEND_ORIGIN}/api/auth/youtube_portability/login/`;
  },

  /**
   * Get current authenticated user
   * @returns {Promise} User object
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/user/');
      // Backend returns { status: 'success', user: {...} } or { status: 'success', authenticated: true, user: {...} }
      const userData = response.data?.user || response.data;
      if (userData) {
        // Store user in localStorage for isAuthenticated() check
        localStorage.setItem('user', JSON.stringify(userData));
      }
      return userData;
    } catch (error) {
      console.error('Get current user error:', error);
      // Don't throw - let caller handle gracefully
      // This prevents redirects when session is still being established
      return null;
    }
  },

  /**
   * Logout current user
   * Clears session and local storage
   */
  logout: async () => {
    try {
      await api.post('/auth/logout/');
      localStorage.removeItem('user');
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local storage even if API call fails
      localStorage.removeItem('user');
      throw error;
    }
  },

  /**
   * Log out and delete stored scan artifacts for the current session platform only
   * (posts, comments, privacy findings, ad insights, and OAuth tokens for that platform).
   */
  logoutAndDeleteSavedData: async () => {
    try {
      const response = await api.post('/auth/logout/', {
        purge_saved_data: true,
        confirm: 'DELETE_MY_DATA',
      });
      localStorage.removeItem('user');
      return response.data;
    } catch (error) {
      console.error('Logout purge error:', error);
      throw error;
    }
  },

  /**
   * Check if user is authenticated
   * Checks both localStorage and attempts to verify with backend session
   * @returns {boolean}
   */
  isAuthenticated: () => {
    const user = localStorage.getItem('user');
    // If user exists in localStorage, consider authenticated
    if (user) {
      return true;
    }
    // Even if localStorage is empty, backend might have session cookie
    // Don't return false immediately - let ProtectedRoute handle it
    // This prevents false negatives right after OAuth when localStorage isn't populated yet
    return false;
  },

  /**
   * Get user from localStorage
   * @returns {object|null}
   */
  getStoredUser: () => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Handle OAuth callback (called from callback page)
   * Backend already processed callback and set session cookie
   */
  handleOAuthCallback: async () => {
    try {
      const user = await authService.getCurrentUser();
      if (!user) {
        return { success: false, error: 'No authenticated user in session' };
      }
      return { success: true, user };
    } catch (error) {
      console.error('OAuth callback error:', error);
      return { success: false, error: error.message };
    }
  },
};

export default authService;
